<div [class.loading]="!imageObjectLoaded" class="image-loading-container">
  <ng-container [ngTemplateOutlet]="loadingTemplate"></ng-container>
</div>

<div
  *ngIf="!!image"
  [hidden]="!imageObjectLoaded"
  class="main-area-container regular-image-viewer overflow-hidden d-flex flex-column w-100 h-100"
>
  <!-- Slideshow mobile header - shown when in a slideshow on mobile and md devices -->
  <div
    *ngIf="deviceService.lgMin() === false && !standalone"
    [class.invisible]="!active"
    class="slideshow-mobile-header d-lg-none"
  >
    <div class="slideshow-mobile-header-container">
      <div class="slideshow-mobile-header-left">
        <button (click)="closeClick.emit()" class="btn btn-icon">
          <fa-icon icon="arrow-left"></fa-icon>
        </button>
      </div>
      <div class="slideshow-mobile-header-spacer"></div>
      <div class="slideshow-mobile-header-right">
        <!-- Mouse-hover button (mdMax only, slideshow mode only) -->
        <button
          *ngIf="!standalone && deviceService.mdMax() && image && imageFileLoaded && hasMouseHover"
          [class.active]="forceViewMouseHover"
          (click)="toggleViewMouseHover()"
          class="btn btn-icon mb-0"
        >
          <fa-icon class="me-0" icon="crosshairs"></fa-icon>
        </button>

        <!-- Toolbox button (mdMax only, slideshow mode only) -->
        <button
          *ngIf="!standalone && deviceService.mdMax() && image && imageFileLoaded"
          (click)="openToolboxOffcanvas()"
          class="btn btn-icon mb-0"
        >
          <fa-icon class="me-0" icon="toolbox"></fa-icon>
        </button>

        <!-- Share button -->
        <astrobin-image-viewer-share-button
          *ngIf="image"
          [image]="image"
          [revisionLabel]="revisionLabel"
        ></astrobin-image-viewer-share-button>

        <!-- Previous button (mdMax only, slideshow mode only) -->
        <button
          *ngIf="!standalone && deviceService.mdMax() && navigationContext && navigationContext.length > 1"
          [disabled]="!showPreviousButton"
          (click)="onArrowLeft($event)"
          class="btn btn-icon mb-0 mobile-nav-button"
        >
          <fa-icon class="me-0" icon="chevron-left"></fa-icon>
        </button>

        <!-- Next button (mdMax only, slideshow mode only) -->
        <button
          *ngIf="!standalone && deviceService.mdMax() && navigationContext && navigationContext.length > 1"
          [disabled]="!showNextButton"
          (click)="onArrowRight($event)"
          class="btn btn-icon mb-0 mobile-nav-button"
        >
          <fa-icon class="me-0" icon="chevron-right"></fa-icon>
        </button>

        <!-- Menu button -->
        <ng-container *ngIf="currentUserWrapper$ | async as currentUserWrapper">
          <button
            *ngIf="shouldShowSlideshowMenu(currentUserWrapper)"
            (click)="registerSlideshowMobileMenu()"
            class="btn btn-icon"
          >
            <fa-icon icon="ellipsis-v"></fa-icon>
          </button>
        </ng-container>
      </div>
    </div>
  </div>

  <div #mainArea class="main-area">
    <ng-container [ngTemplateOutlet]="imageAreaTemplate"></ng-container>

    <div class="data-area-container">
      <ng-container [ngTemplateOutlet]="dataAreaTemplate"></ng-container>
      <div class="data-area-bottom-gradient"></div>
    </div>
    <!-- data-area-container -->
  </div>

  <astrobin-scroll-to-top
    #scrollToTopMdMax
    *ngIf="!deviceService.lgMin()"
    [adjustBottomWithMobileFooter]="false"
    [element]="mainArea"
  ></astrobin-scroll-to-top>

  <astrobin-scroll-to-top
    #scrollToTopLgMin
    *ngIf="deviceService.lgMin()"
    [adjustBottomWithMobileFooter]="false"
    [element]="dataArea?.nativeElement"
  ></astrobin-scroll-to-top>
</div>

<ng-template #loadingTemplate>
  <astrobin-loading-indicator></astrobin-loading-indicator>
</ng-template>

<ng-template #mobileMenuTitleTemplate>
  {{ "Image" | translate }}
</ng-template>

<ng-template #mobileMenuIconsTemplate>
  <!-- No icons needed in the top right corner of the offcanvas - already has X button built in -->
</ng-template>

<ng-template #navTemplate let-image>
  <astrobin-image-viewer-menu [image]="image" [revisionLabel]="revisionLabel"></astrobin-image-viewer-menu>
</ng-template>

<ng-template #imageAreaTemplate>
  <div
    #imageArea
    [class.adjustment-editor-visible]="adjustmentEditorVisible"
    [class.force-view-annotations-mousehover]="forceViewAnnotationsMouseHover"
    [class.force-view-mousehover]="forceViewMouseHover"
    [class.has-top-banner]="image && image.isIotd"
    [class.has-url-annotations]="hasUrlAnnotations"
    [style.--image-area-height]="imageAreaFixedHeight ? imageAreaFixedHeight + 'px' : null"
    (mouseenter)="onImageAreaMouseEnter()"
    (mouseleave)="onImageAreaMouseLeave()"
    class="image-area"
  >
    <div class="image-area-header">
      <astrobin-image-viewer-iotd-banner
        *ngIf="image && image.isIotd"
        [image]="image"
      ></astrobin-image-viewer-iotd-banner>
    </div>

    <div class="image-area-body">
      <!-- Moon scale overlay -->
      <div
        *ngIf="showMoonOverlay && moonScaleDiameter > 0"
        @fadeInOut
        [class.dragging]="isDraggingMoon"
        [style.left]="moonPosition.x + '%'"
        [style.top]="moonPosition.y + '%'"
        (pointerdown)="onMoonDragStart($event)"
        class="moon-scale-overlay"
        data-ignore-swipe-down="true"
      >
        <!-- Placeholder circle shown while image is loading -->
        <div
          *ngIf="!isMoonImageLoaded"
          [style.height.px]="moonScaleDiameter"
          [style.width.px]="moonScaleDiameter"
          class="moon-placeholder"
        ></div>

        <!-- Actual moon image -->
        <img
          [class.hidden]="!isMoonImageLoaded"
          [height]="moonScaleDiameter"
          [src]="moonImageSrc"
          [width]="moonScaleDiameter"
          (error)="isMoonImageLoaded = false"
          (load)="onMoonImageLoaded()"
          class="moon-image"
          alt="Moon scale"
          draggable="false"
        />

        <div class="moon-label-container">
          <span class="moon-label">{{ "Moon scale" | translate }}</span>
          <div class="divider"></div>
          <div class="moon-controls">
            <span
              [ngbPopover]="moonInfoPopover"
              class="icon-btn info-icon"
              container="body"
              placement="bottom"
              popoverClass="moon-info-popover"
              title="{{ 'About moon scale' | translate }}"
              triggers="click hover"
            >
              <fa-icon icon="info-circle"></fa-icon>
            </span>
            <button
              (click)="$event.stopPropagation(); resetMoonOverlay()"
              class="icon-btn close-moon-btn"
              title="{{ 'Close' | translate }}"
            >
              <fa-icon icon="times"></fa-icon>
            </button>
          </div>
        </div>

        <ng-template #moonInfoPopover>
          <div class="moon-info-content">
            {{
              "This scale represents the apparent size of the full moon (0.52° diameter) based on the plate-solved pixel scale of the image. The actual apparent size of the moon varies slightly throughout the year due to its elliptical orbit."
                | translate
            }}
          </div>
        </ng-template>
      </div>

      <ng-container *ngIf="adjustmentEditorVisible && isBrowser">
        <astrobin-image-viewer-adjustment-editor
          [image]="image"
          [imageComponent]="imageComponent"
          [revisionLabel]="revisionLabel"
          (closeClick)="adjustmentEditorVisible = false"
          class="adjustment-editor"
        ></astrobin-image-viewer-adjustment-editor>
      </ng-container>

      <ng-container *ngIf="!adjustmentEditorVisible">
        <!-- North arrow -->
        <div
          *ngIf="
            (showNorthArrow || (forceViewMouseHover && (inlineSvg || solutionMouseHoverImage))) &&
            revision?.solution?.orientation !== null
          "
          @fadeInOut
          [ngStyle]="{
            transform: 'translateX(-50%) rotate(' + northArrowRotation * -1 + 'deg)'
          }"
          [style.display]="additionalButtons?.isCollapsed ? '' : 'none'"
          class="north-arrow-container"
        >
          <div class="north-arrow-wrapper">
            <img class="north-arrow" alt="Compass" src="assets/images/compass.svg?v=1" />
            <div
              [ngbTooltip]="'Celestial North' | translate"
              [ngStyle]="{
                transform: 'rotate(' + northArrowRotation + 'deg)'
              }"
              class="north-arrow-label"
              container="body"
            >
              N
            </div>
          </div>
        </div>

        <astrobin-image-viewer-close-button
          (closeClick)="handleCloseButton($event)"
          class="close-button d-none d-lg-flex justify-content-center align-items-center"
        ></astrobin-image-viewer-close-button>

        <astrobin-image-viewer-additional-buttons
          #additionalButtons
          *ngIf="image && imageFileLoaded"
          [annotationsActive]="isAnnotationMode"
          [annotationsReadOnly]="annotationReadOnlyMode"
          [forceViewAnnotationsMouseHover]="forceViewAnnotationsMouseHover"
          [forceViewMouseHover]="forceViewMouseHover"
          [hasAdvancedSolutionMatrix]="hasValidSolutionMatrix"
          [hasMouseHover]="
            revision.mouseHoverImage !== MouseHoverImageOptions.NOTHING &&
            (!!nonSolutionMouseHoverImage || !!solutionMouseHoverImage || !!inlineSvg)
          "
          [hasUrlAnnotations]="hasUrlAnnotations"
          [image]="image"
          [moonOverlayActive]="showMoonOverlay"
          [navigationContext]="navigationContext"
          [revisionLabel]="revisionLabel"
          [showAnnotationsOnMouseHover]="imageViewerService.showAnnotationsOnMouseHover"
          [standalone]="standalone"
          (showAdjustmentsEditor)="adjustmentEditorVisible = true"
          (toggleAnnotationEditMode)="toggleAnnotationEditMode()"
          (toggleAnnotations)="toggleAnnotationMode($event)"
          (toggleAnnotationsOnMouseHover)="imageViewerService.toggleShowAnnotationsOnMouseHover()"
          (toggleMeasurementTool)="enterFullscreenWithMeasurementTool($event)"
          (toggleMoonOverlay)="toggleMoonOverlay()"
          (toggleViewAnnotationsMouseHover)="toggleForceViewAnnotationsMouseHover()"
          (toggleViewMouseHover)="toggleViewMouseHover()"
          class="additional-buttons"
        >
        </astrobin-image-viewer-additional-buttons>
      </ng-container>

      <button
        *ngIf="navigationContext && navigationContext.length > 1"
        [disabled]="!showPreviousButton"
        (click)="onArrowLeft($event)"
        class="previous-button"
      >
        <fa-icon icon="chevron-left"></fa-icon>
      </button>

      <astrobin-fullscreen-image-viewer
        #fullscreenViewer
        *ngIf="standalone && supportsFullscreen"
        [eagerLoading]="true"
        [externalSolutionMatrix]="advancedSolutionMatrix"
        [id]="image.pk"
        [revisionLabel]="revisionLabel"
        [standalone]="standalone"
        (exitFullscreen)="exitFullscreen()"
      ></astrobin-fullscreen-image-viewer>

      <!-- Annotation tool component - always read-only in regular view -->
      <astrobin-annotation-tool
        *ngIf="isBrowser && (isAnnotationMode || hasUrlAnnotations || hasSavedAnnotations) && imageFileLoaded && active"
        [active]="isAnnotationMode || hasUrlAnnotations || hasSavedAnnotations"
        [hasUrlAnnotations]="hasUrlAnnotations"
        [imageElement]="imageElementForAnnotation"
        [imageId]="image.pk"
        [isImageOwner]="currentUserIsImageOwner"
        [isRegularView]="true"
        [naturalHeight]="revision?.h"
        [naturalWidth]="revision?.w"
        [readOnly]="true"
        [revision]="revision"
        [setMouseOverUIElement]="setMouseOverUIElement.bind(this)"
        [windowHeight]="windowRefService.nativeWindow.innerHeight"
        [windowWidth]="windowRefService.nativeWindow.innerWidth"
        (annotationsSaved)="onAnnotationsSaved($event)"
        (exitAnnotationMode)="onExitAnnotationMode()"
        (readOnlyAnnotationClick)="enterFullscreen($event, { enableAnnotations: true })"
        (requestFullscreenMode)="enterFullscreen(null)"
        class="measure-annotation-overlay"
      ></astrobin-annotation-tool>

      <astrobin-image
        #imageComponent
        @fadeInOut
        [alias]="alias"
        [autoHeight]="true"
        [class.supports-fullscreen]="supportsFullscreen"
        [forceLoad]="true"
        [image]="image"
        [revisionLabel]="revisionLabel"
        (imageClick)="enterFullscreen($event)"
        (imageMouseEnter)="onImageMouseEnter($event)"
        (imageMouseLeave)="onImageMouseLeave($event)"
        (imageTouchstart)="enterFullscreen($event)"
        (loaded)="onImageLoaded()"
      ></astrobin-image>

      <!--
        If the mouse-hover settings indicate a non-solution mouse-hover, we always show it on hover. Except if the
        solution mouse-hover is being forced.
      -->
      <div
        *ngIf="
          imageFileLoaded && revision.mouseHoverImage !== MouseHoverImageOptions.SOLUTION && nonSolutionMouseHoverImage
        "
        [class.has-revisions]="image?.revisions?.length > 0"
        (intentionalClick)="enterFullscreen($event)"
        class="mouse-hover-container"
        astrobinPreventScrollClick
      >
        <img
          [style.max-width.px]="mouseHoverOptimalMaxWidth"
          (mouseenter)="onImageMouseEnter($event)"
          (mouseleave)="onImageMouseLeave($event)"
          class="mouse-hover ready"
          alt=""
          src="{{ nonSolutionMouseHoverImage }}"
        />
      </div>

      <!--
        If the mouse-hover settings indicate a solution mouse-hover, and there is one available, we show it according to
        mouse-hover settings (hover on image vs hover on button).
      -->
      <div
        *ngIf="
          imageFileLoaded &&
          (revision.mouseHoverImage === MouseHoverImageOptions.SOLUTION || forceViewAnnotationsMouseHover) &&
          solutionMouseHoverImage &&
          !inlineSvg
        "
        [class.has-revisions]="image?.revisions?.length > 0"
        (intentionalClick)="enterFullscreen($event)"
        class="mouse-hover-container"
        astrobinPreventScrollClick
      >
        <img
          [style.max-width.px]="mouseHoverOptimalMaxWidth"
          (mouseenter)="onImageMouseEnter($event)"
          (mouseleave)="onImageMouseLeave($event)"
          class="mouse-hover ready"
          alt=""
          src="{{ solutionMouseHoverImage }}"
        />
      </div>

      <div
        *ngIf="
          imageFileLoaded &&
          (revision.mouseHoverImage === MouseHoverImageOptions.SOLUTION || forceViewAnnotationsMouseHover) &&
          inlineSvg
        "
        (intentionalClick)="enterFullscreen($event)"
        class="mouse-hover-svg-container"
        astrobinPreventScrollClick
      >
        <div
          [id]="'mouse-hover-svg-' + image.pk + '-' + revision.pk"
          [innerHTML]="inlineSvg"
          [showGalactic]="true"
          [solutionMatrix]="advancedSolutionMatrix"
          (coords)="onCoordsChange($event, $event?.event)"
          (mouseenter)="onImageMouseEnter($event)"
          (mouseleave)="onImageMouseLeave($event)"
          class="mouse-hover w-100 position-relative"
          astrobinCoordinatesTracker
        ></div>

        <div class="mouse-hover-svg-overlay">
          <astrobin-loading-indicator
            *ngIf="!advancedSolutionMatrix && loadingAdvancedSolutionMatrix"
          ></astrobin-loading-indicator>

          <astrobin-coordinates-display
            *ngIf="!!advancedSolutionMatrix && mouseCoords"
            [decHtml]="mouseCoords?.decHtml"
            [galacticDecHtml]="mouseCoords?.galacticDecHtml"
            [galacticRaHtml]="mouseCoords?.galacticRaHtml"
            [raHtml]="mouseCoords?.raHtml"
            [showAttribution]="true"
            [showGalactic]="true"
            ngbTooltip="RA/Dec"
          ></astrobin-coordinates-display>
        </div>

        <div [style.top.px]="mouseHoverY" class="x-ruler"></div>

        <div [style.left.px]="mouseHoverX" class="y-ruler"></div>
      </div>

      <button
        *ngIf="navigationContext && navigationContext.length > 1"
        [disabled]="!showNextButton"
        (click)="onArrowRight($event)"
        class="next-button"
      >
        <fa-icon icon="chevron-right"></fa-icon>
      </button>

      <astrobin-image-viewer-revisions
        [activeLabel]="revisionLabel"
        [image]="image"
        (revisionSelected)="onRevisionSelected($event, true)"
      ></astrobin-image-viewer-revisions>
    </div>
  </div>
</ng-template>

<ng-template #descriptionTemplate let-currentUser="currentUser">
  <ng-container *ngIf="image?.descriptionBbcode || image?.descriptionHtml || image?.description">
    <div class="metadata-header">{{ "Description" | translate }}</div>

    <div *ngIf="translatedDescription; else noTranslationTemplate" class="metadata-section flex-wrap">
      <div class="metadata-item description translated-content">
        <small class="text-muted fst-italic d-block mb-2">{{ "Translated" | translate }}</small>
        <div [innerHTML]="translatedDescription | fixLegacyUserUrls" (click)="onDescriptionClicked($event)"></div>
      </div>
    </div>

    <ng-template #noTranslationTemplate>
      <div *ngIf="image?.descriptionHtml; else descriptionBbcodeTemplate" class="metadata-section flex-wrap">
        <div
          [innerHTML]="image.descriptionHtml | fixLegacyUserUrls | highlight: searchModel?.text?.value : true"
          (click)="onDescriptionClicked($event)"
          class="metadata-item description"
        ></div>
      </div>
    </ng-template>

    <ng-template #descriptionBbcodeTemplate>
      <ng-container *ngIf="image.descriptionBbcode; else plainDescriptionTemplate">
        <div
          *ngIf="image.descriptionBbcode | BBCodeToHtml | async as html; else descriptionBbcodeLoadingTemplate"
          class="metadata-section flex-wrap"
        >
          <div
            [innerHTML]="html | highlight: searchModel?.text?.value : true"
            (click)="onDescriptionClicked($event)"
            class="metadata-item description"
          ></div>
        </div>
      </ng-container>
    </ng-template>

    <ng-template #descriptionBbcodeLoadingTemplate>
      <div class="metadata-section flex-wrap">
        <div class="metadata-item description py-4 mb-4">
          <small>
            <fa-icon animation="spin" icon="spinner"></fa-icon>
            {{ "Loading content..." | translate }}
          </small>
        </div>
      </div>
    </ng-template>

    <ng-template #plainDescriptionTemplate>
      <div *ngIf="image?.description" class="metadata-section flex-wrap">
        <div
          [innerHTML]="image.description | highlight: searchModel?.text?.value : true"
          (click)="onDescriptionClicked($event)"
          class="metadata-item description"
        ></div>
      </div>
    </ng-template>

    <button
      *ngIf="
        image &&
        image.detectedLanguage &&
        image.detectedLanguage !== translateService.currentLang &&
        (image.descriptionHtml || image.descriptionBbcode || image.description) &&
        !translatedDescription &&
        currentUser
      "
      (click)="onTranslateDescriptionClicked($event)"
      class="btn btn-link text-muted btn-no-block w-auto text-start ms-2 mb-5 btn-translate-description"
    >
      <fa-icon icon="language"></fa-icon>
      {{ "Translate" | translate }}

      <fa-icon *ngIf="translatingDescription" @fadeInOut class="ms-2" animation="spin" icon="circle-notch"></fa-icon>
    </button>

    <button
      *ngIf="translatedDescription"
      (click)="onSeeOriginalDescriptionClicked($event)"
      class="btn btn-link text-muted btn-no-block w-auto text-start ms-2 mb-5 btn-translate-description"
    >
      <fa-icon icon="language"></fa-icon>
      {{ "See original" | translate }}
    </button>
  </ng-container>
</ng-template>

<ng-template #moreFromThisUserTemplate>
  <div class="metadata-header mb-3">{{ "More from this user" | translate }}</div>
  <astrobin-image-search
    [loadMoreOnScroll]="false"
    [model]="{ userId: image.user, ordering: '-likes', pageSize: 25 }"
    [showMarketplaceItems]="false"
    [showRetailers]="false"
    [showStaticOverlay]="false"
    class="more-from-this-photographer mb-5"
  ></astrobin-image-search>
</ng-template>

<ng-template #similarToThisTemplate>
  <div class="metadata-header mb-3">
    <ng-container
      *ngIf="revision?.solution?.ra !== null && revision?.solution?.dec !== null; else relatedImagesLabelTemplate"
    >
      {{ "Images in the same area" | translate }}
    </ng-container>
    <ng-template #relatedImagesLabelTemplate>
      {{ "Related images" | translate }}
    </ng-template>
  </div>
  <astrobin-image-search
    [loadMoreOnScroll]="true"
    [model]="{ similarToImageId: this.image.hash || this.image.pk, ordering: '-likes', pageSize: 50 }"
    [showMarketplaceItems]="false"
    [showRetailers]="false"
    [showStaticOverlay]="false"
    class="similar-to-this"
  ></astrobin-image-search>
</ng-template>

<ng-template #upgradeToPlateSolveBannerMessageTemplate>
  <div class="flex-grow-1">
    <fa-icon class="me-2" icon="exclamation-triangle"></fa-icon>
    <span>{{ "Upgrade your account to enable plate-solving." | translate }}</span>
  </div>
  <a [routerLink]="['/subscriptions/options']">
    {{ "Learn more" | translate }}
  </a>
</ng-template>

<ng-template #dataAreaTemplate>
  <div #dataArea *ngIf="currentUserWrapper$ | async as currentUserWrapper" [class.d-none]="!adConfig" class="data-area">
    <astrobin-ad-manager
      #ad
      *ngIf="adConfig && (standalone || active)"
      [class.d-none]="!showAd || !active"
      [configName]="adConfig"
      (adDisplayed)="adDisplayed = true"
    ></astrobin-ad-manager>

    <astrobin-image-viewer-wip-banner
      *ngIf="image && image.isWip && currentUserWrapper.user?.id === image.user"
      [image]="image"
      class="image-viewer-banner-component"
    ></astrobin-image-viewer-wip-banner>

    <astrobin-image-viewer-plate-solving-banner
      *ngIf="
        image &&
        !revision?.videoFile &&
        showPlateSolvingBanner &&
        (currentUserWrapper.user?.id === image.user || currentUserWrapper.user?.isSuperUser)
      "
      [image]="image"
      [revisionLabel]="revisionLabel"
      (solutionChange)="onSolutionChange($event)"
      class="image-viewer-banner-component"
    ></astrobin-image-viewer-plate-solving-banner>

    <astrobin-image-viewer-custom-message-banner
      *ngIf="(showUpgradeToPlateSolveBanner$ | async) === true"
      [messageTemplate]="upgradeToPlateSolveBannerMessageTemplate"
      class="image-viewer-banner-component"
      alertClass="info"
    ></astrobin-image-viewer-custom-message-banner>

    <astrobin-image-viewer-title
      [image]="image"
      [revisionLabel]="revisionLabel"
      [searchModel]="searchModel"
    ></astrobin-image-viewer-title>

    <astrobin-image-viewer-floating-title #floatingTitle [image]="image"></astrobin-image-viewer-floating-title>

    <astrobin-image-viewer-photographers
      *ngIf="userContentType"
      [image]="image"
      [revision]="revision"
      [userContentType]="userContentType"
    ></astrobin-image-viewer-photographers>

    <div class="metadata-striped">
      <astrobin-image-viewer-groups-and-collections
        *ngIf="image?.partOfGroupSet?.length || image?.collections?.length"
        [image]="image"
      ></astrobin-image-viewer-groups-and-collections>

      <astrobin-image-viewer-data-source
        [image]="image"
        [searchModel]="searchModel"
      ></astrobin-image-viewer-data-source>

      <astrobin-image-viewer-astrometry
        *ngIf="revision?.solution?.ra !== null && revision?.solution?.dec !== null"
        [image]="image"
        [revisionLabel]="revisionLabel"
        [searchModel]="searchModel"
      ></astrobin-image-viewer-astrometry>
    </div>

    <astrobin-image-viewer-acquisition
      *ngIf="image?.deepSkyAcquisitions?.length || image?.solarSystemAcquisitions?.length"
      [image]="image"
      [searchModel]="searchModel"
    ></astrobin-image-viewer-acquisition>

    <astrobin-image-viewer-equipment
      *ngIf="image && imageService.hasEquipment(image)"
      [image]="image"
      [searchModel]="searchModel"
    ></astrobin-image-viewer-equipment>

    <astrobin-image-viewer-guiding-equipment
      *ngIf="image && imageService.hasGuidingEquipment(image)"
      [image]="image"
    ></astrobin-image-viewer-guiding-equipment>

    <astrobin-image-viewer-objects
      *ngIf="revision?.solution?.objectsInField || revision?.solution?.advancedAnnotations"
      [image]="image"
      [revisionLabel]="revisionLabel"
      [searchModel]="searchModel"
    ></astrobin-image-viewer-objects>

    <ng-container
      [ngTemplateOutlet]="descriptionTemplate"
      [ngTemplateOutletContext]="{
        currentUser: currentUserWrapper.user
      }"
    ></ng-container>

    <astrobin-image-viewer-revision-summary
      *ngIf="image?.revisions?.length > 0"
      [revision]="revision"
    ></astrobin-image-viewer-revision-summary>

    <ng-container [ngTemplateOutlet]="nestedCommentsTemplate"></ng-container>

    <ng-container [ngTemplateOutlet]="moreFromThisUserTemplate"></ng-container>

    <ng-container [ngTemplateOutlet]="similarToThisTemplate"></ng-container>
  </div>
  <!-- data-area -->
</ng-template>

<ng-template #nestedCommentsTemplate>
  <div class="image-viewer-comments-header metadata-header mb-3">{{ "Comments" | translate }}</div>

  <ng-container *ngIf="image && !image.allowComments">
    <p class="px-2">
      {{ "The photographer has not enabled comments for this image." | translate }}
    </p>
  </ng-container>

  <ng-container *ngIf="image?.allowComments">
    <ng-container *ngIf="currentUserWrapper$ | async as currentUserWrapper">
      <astrobin-nested-comments
        *ngIf="imageContentType; else loadingTemplate"
        [allowModeration]="currentUserWrapper.user?.id === image.user"
        [contentType]="imageContentType"
        [objectId]="image.pk"
        [showCloseButton]="false"
        [useOffcanvasOnTouchDevices]="true"
        class="d-block mb-5"
      ></astrobin-nested-comments>
    </ng-container>
  </ng-container>
</ng-template>

<ng-template #toolboxOffcanvasTemplate let-offcanvas>
  <div class="offcanvas-header">
    <h5 class="offcanvas-title">{{ "Image tools" | translate }}</h5>
    <button (click)="offcanvas.dismiss()" class="btn-close" aria-label="Close" type="button"></button>
  </div>
  <div class="offcanvas-body">
    <div class="toolbox-menu">
      <!-- Plate-solving annotations toggle (desktop only, when hover is available) -->
      <div *ngIf="!deviceService.isTouchEnabled() && hasMouseHover" class="toolbox-menu-item toolbox-toggle-item">
        <astrobin-toggle-button
          [label]="'Plate-solving annotations on hover' | translate"
          [value]="showAnnotationsOnMouseHover"
          (toggle)="toggleShowAnnotationsOnMouseHover()"
        ></astrobin-toggle-button>
      </div>

      <!-- Moon scale overlay -->
      <button
        *ngIf="revision?.solution?.pixscale"
        [class.active]="showMoonOverlay"
        (click)="toggleMoonOverlay(); offcanvas.dismiss()"
        class="toolbox-menu-item"
      >
        <fa-icon icon="moon"></fa-icon>
        <span class="label">{{
          showMoonOverlay ? ("Hide moon scale" | translate) : ("Show moon scale for a size comparison" | translate)
        }}</span>
      </button>

      <!-- Sky map viewer -->
      <button
        *ngIf="revision?.solution && (revision?.solution.skyplotZoom1 || revision?.solution.pixinsightFindingChart)"
        (click)="openSkyplotModal(); offcanvas.dismiss()"
        class="toolbox-menu-item"
      >
        <fa-icon icon="map"></fa-icon>
        <span class="label">{{ "View sky map" | translate }}</span>
      </button>

      <!-- Histogram viewer -->
      <button
        *ngIf="!revision?.videoFile"
        (click)="openHistogramModal(); offcanvas.dismiss()"
        class="toolbox-menu-item"
      >
        <fa-icon icon="chart-simple"></fa-icon>
        <span class="label">{{ "View histogram" | translate }}</span>
      </button>

      <!-- Measurement tool -->
      <button
        *ngIf="!revision?.videoFile"
        [class.disabled]="!hasValidSolutionMatrix"
        [disabled]="!hasValidSolutionMatrix"
        (click)="hasValidSolutionMatrix && toggleMeasurementTool(); offcanvas.dismiss()"
        class="toolbox-menu-item"
      >
        <fa-icon icon="ruler"></fa-icon>
        <span class="label">{{
          hasValidSolutionMatrix
            ? ("Measure distances" | translate)
            : ("The measuring tool is only available on images solved with PixInsight." | translate)
        }}</span>
      </button>

      <!-- Annotations -->
      <button
        *ngIf="!revision?.videoFile"
        [class.active]="hasUrlAnnotations"
        (click)="toggleAnnotations(); offcanvas.dismiss()"
        class="toolbox-menu-item"
      >
        <fa-icon icon="file-text"></fa-icon>
        <span class="label">{{
          hasUrlAnnotations ? ("Annotations from URL available" | translate) : ("Annotate image" | translate)
        }}</span>
      </button>

      <!-- Image adjustments (md+ only) -->
      <button
        *ngIf="
          (image.allowImageAdjustmentsWidget === true ||
            (image.allowImageAdjustmentsWidget === null && image.defaultAllowImageAdjustmentsWidget)) &&
          deviceService.mdMin()
        "
        (click)="showAdjustmentsEditor(); offcanvas.dismiss()"
        class="toolbox-menu-item"
      >
        <fa-icon icon="sliders"></fa-icon>
        <span class="label">{{ "Image adjustments" | translate }}</span>
      </button>
    </div>
  </div>
</ng-template>
